/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';

import { endpoints } from '../../../ia/util/config';
import { PHONE_CHANNEL_TYPE } from '../../util/channelTypes';
import { generateSubscribeParams } from './utils';

export const baseUrl = urlJoin(
  endpoints ? endpoints.store : '',
  'v2',
  'service_desks'
);

export const buildSubEndpoint = (
  integrationType: string,
  integrationId: string
): string =>
  urlJoin(
    integrationType.toLowerCase() === 'wwx'
      ? 'chat'
      : integrationType.toLowerCase(),
    integrationId
  );

export const getServiceDesk = (
  integrationType: string,
  integrationId: string
): Promise<any> =>
  axios
    .get(
      urlJoin(
        baseUrl,
        buildSubEndpoint(integrationType, integrationId),
        'service_desk'
      )
    )
    .then(response => response.data);

export const subscribe = (
  integrationType: string,
  integrationId: string,
  data: any,
  isUniqueKey?: boolean
) =>
  axios
    .post(
      urlJoin(
        baseUrl,
        buildSubEndpoint(integrationType, integrationId),
        'subscribe'
      ),
      data,
      {
        params: generateSubscribeParams(isUniqueKey)
      }
    )
    .then(response => response.data);

export const unsubscribe = (
  integrationType: string,
  integrationId: string
): Promise<void> => {
  return axios
    .post(
      urlJoin(
        baseUrl,
        buildSubEndpoint(integrationType, integrationId),
        'unsubscribe'
      ),
      {}
    )
    .then(response => response.data);
};

// API call to get the voice piece mainly used in Action preview
export const synthesize = (integrationId: string, inputData) => {
  return axios.post(
    urlJoin(
      baseUrl,
      buildSubEndpoint(PHONE_CHANNEL_TYPE, integrationId),
      'synthesize'
    ),
    inputData,
    {
      responseType: 'arraybuffer',
      params: generateSubscribeParams()
    }
  );
};

// Returns the URL of the endpoint that downloads the agent application zip file.
// This URL needs to be spawned in a separate window, hence why this method returns only the URL.
export const agentApplicationUrl = (
  integrationType: string,
  integrationId: string
) => {
  return urlJoin(
    baseUrl,
    buildSubEndpoint(integrationType, integrationId),
    'agent_application'
  );
};
